import type { Guide as RCGuideLine } from '@/shared/graphics/RenderComponents/RCGuides';

import type {
  FullHeadMprSliceGroupView,
  FullHeadMprSliceGuideView,
} from '../config';
import {
  CLIENT_SLICE_GUIDE_SOURCES,
  GUIDE_AXIS_EPSILON,
  SERIES_OFFSET_AXIS,
} from '../config';

import { clipLineToFrame } from './clipLineToFrame';

const mid = (range: { max: number; min: number }) =>
  (range.min + range.max) / 2;

const makeGuideLines = (
  group: FullHeadMprSliceGroupView,
  source: FullHeadMprSliceGroupView,
  sourceTileOffsetMm: number,
): RCGuideLine[] => {
  const axisName = SERIES_OFFSET_AXIS[group.seriesName];
  const frame = source.frame;
  const a = frame.uAxisLocal[axisName];
  const b = frame.vAxisLocal[axisName];

  if (Math.abs(a) < GUIDE_AXIS_EPSILON && Math.abs(b) < GUIDE_AXIS_EPSILON) {
    return [];
  }

  const frameCenterProjection =
    mid(frame.uRangeMm) * a +
    mid(frame.vRangeMm) * b +
    sourceTileOffsetMm * frame.normalLocal[axisName];

  return group.tiles.reduce<RCGuideLine[]>((lines, tile) => {
    const line = clipLineToFrame({
      a,
      b,
      c: tile.offsetMm - frameCenterProjection,
      frame,
      tileID: tile.id,
    });

    return line ? [...lines, line] : lines;
  }, []);
};

export const makeFullHeadMprSliceGuides = ({
  group,
  groups,
}: {
  group: FullHeadMprSliceGroupView;
  groups: FullHeadMprSliceGroupView[];
}): FullHeadMprSliceGuideView[] =>
  CLIENT_SLICE_GUIDE_SOURCES[group.seriesName].reduce<
    FullHeadMprSliceGuideView[]
  >((guides, sourceName) => {
    const source = groups.find(
      (candidate) => candidate.seriesName === sourceName,
    );

    if (!source?.tiles.length) {
      return guides;
    }

    const centralTile = source.tiles[Math.floor(source.tiles.length / 2)];

    return [
      ...guides,
      {
        id: `${centralTile.id}:guide-for-${group.seriesName}`,
        imageHeightPx: source.frame.heightPx,
        imageWidthPx: source.frame.widthPx,
        lines: makeGuideLines(group, source, centralTile.offsetMm),
        pngUrl: centralTile.pngUrl,
      },
    ];
  }, []);
